import { fromUnixTime } from 'date-fns'
import { PairDayDatasResponse, PairHoursDatasResponse } from './fetch/types'
import { DerivedPairDataNormalized, PairDataNormalized, PairDataTimeWindowEnum, PairPricesNormalized } from './types'

export const normalizeChartData = (
  data: PairHoursDatasResponse | PairDayDatasResponse | null,
  timeWindow: PairDataTimeWindowEnum,
) => {
  if (!data) {
    console.warn('[normalizeChartData] No data provided')
    return null
  }

  switch (timeWindow) {
    case PairDataTimeWindowEnum.DAY:
    case PairDataTimeWindowEnum.WEEK: {
      const pairHourDatas = (data as PairHoursDatasResponse)?.pairHourDatas
      if (!pairHourDatas) {
        console.warn('[normalizeChartData] No pairHourDatas found for DAY/WEEK timeWindow')
        return null
      }
      
      const normalizedData = pairHourDatas.map((fetchPairEntry) => {
        if (!fetchPairEntry.pair?.token0?.id || !fetchPairEntry.pair?.token1?.id) {
          console.warn('[normalizeChartData] Missing token data in pairHourDatas entry:', fetchPairEntry)
          return null
        }
        return {
          time: fetchPairEntry.hourStartUnix,
          token0Id: fetchPairEntry.pair.token0.id,
          token1Id: fetchPairEntry.pair.token1.id,
          reserve0: parseFloat(fetchPairEntry.reserve0),
          reserve1: parseFloat(fetchPairEntry.reserve1),
        }
      }).filter(Boolean) // Remove null entries
      
      return normalizedData
    }
    case PairDataTimeWindowEnum.MONTH:
    case PairDataTimeWindowEnum.YEAR: {
      const pairDayDatas = (data as PairDayDatasResponse)?.pairDayDatas
      if (!pairDayDatas) {
        console.warn('[normalizeChartData] No pairDayDatas found for MONTH/YEAR timeWindow')
        return null
      }
      
      const normalizedData = pairDayDatas.map((fetchPairEntry) => {
        if (!fetchPairEntry.pairAddress?.token0?.id || !fetchPairEntry.pairAddress?.token1?.id) {
          console.warn('[normalizeChartData] Missing token data in pairDayDatas entry:', fetchPairEntry)
          return null
        }
        return {
          time: fetchPairEntry.date,
          token0Id: fetchPairEntry.pairAddress.token0.id,
          token1Id: fetchPairEntry.pairAddress.token1.id,
          reserve0: parseFloat(fetchPairEntry.reserve0),
          reserve1: parseFloat(fetchPairEntry.reserve1),
        }
      }).filter(Boolean) // Remove null entries
      
      return normalizedData
    }
    default:
      return null
  }
}

export const normalizeDerivedChartData = (data: any) => {
  if (!data?.token0DerivedBnb || data?.token0DerivedBnb.length === 0) {
    console.warn('[normalizeDerivedChartData] No token0DerivedBnb data provided')
    return []
  }
  
  if (!data?.token1DerivedBnb || data?.token1DerivedBnb.length === 0) {
    console.warn('[normalizeDerivedChartData] No token1DerivedBnb data provided')
    return []
  }
  
  return data?.token0DerivedBnb.reduce((acc, token0DerivedBnbEntry) => {
    if (!token0DerivedBnbEntry?.timestamp || !token0DerivedBnbEntry?.tokenAddress) {
      console.warn('[normalizeDerivedChartData] Invalid token0DerivedBnbEntry:', token0DerivedBnbEntry)
      return acc
    }
    
    const token1DerivedBnbEntry = data?.token1DerivedBnb?.find(
      (entry) => entry?.timestamp === token0DerivedBnbEntry.timestamp,
    )
    if (!token1DerivedBnbEntry?.tokenAddress) {
      console.warn('[normalizeDerivedChartData] No matching token1DerivedBnbEntry found for timestamp:', token0DerivedBnbEntry.timestamp)
      return acc
    }
    return [
      ...acc,
      {
        time: parseInt(token0DerivedBnbEntry.timestamp, 10),
        token0Id: token0DerivedBnbEntry.tokenAddress,
        token1Id: token1DerivedBnbEntry.tokenAddress,
        token0DerivedBNB: token0DerivedBnbEntry.derivedBNB,
        token1DerivedBNB: token1DerivedBnbEntry.derivedBNB,
      },
    ]
  }, [])
}

type normalizePairDataByActiveTokenParams = {
  pairData: PairDataNormalized
  activeToken: string
}

export const normalizePairDataByActiveToken = ({
  pairData,
  activeToken,
}: normalizePairDataByActiveTokenParams): PairPricesNormalized =>
  pairData
    ?.map((pairPrice) => ({
      time: fromUnixTime(pairPrice.time),
      value:
        activeToken === pairPrice?.token0Id
          ? pairPrice.reserve1 / pairPrice.reserve0
          : pairPrice.reserve0 / pairPrice.reserve1,
    }))
    .reverse()

type normalizeDerivedPairDataByActiveTokenParams = {
  pairData: DerivedPairDataNormalized
  activeToken: string
}

export const normalizeDerivedPairDataByActiveToken = ({
  pairData,
  activeToken,
}: normalizeDerivedPairDataByActiveTokenParams): PairPricesNormalized =>
  pairData?.map((pairPrice) => ({
    time: fromUnixTime(pairPrice.time),
    value:
      activeToken === pairPrice?.token0Id
        ? pairPrice.token0DerivedBNB / pairPrice.token1DerivedBNB
        : pairPrice.token1DerivedBNB / pairPrice.token0DerivedBNB,
  }))
